import YouTube from 'react-youtube';

import { Box, Flex, Heading, List, ListItem, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { trackEvent } from '~/features/analytics/track-event';

export type SponsorAScholarshipSectionProps = {
  title: string;
  description: string;
  video: {
    _id: string;
    youTubeVideoId: string;
    title: string;
  } | null;
  organizationBenefits: Array<{
    _id: string;
    glyphName: string;
    title: string;
    description: string;
  }>;
  communityBenefits: Array<{
    _id: string;
    glyphName: string;
    title: string;
    description: string;
  }>;
};

export function SponsorAScholarshipSection({
  title,
  description,
  video,
  organizationBenefits,
  communityBenefits,
}: SponsorAScholarshipSectionProps) {
  return (
    <Box
      as='section'
      backgroundColor='white'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer alignItems='center' display='flex' flexDir='column'>
        <Box
          maxWidth='734px'
          textAlign='center'
          margin={{
            base: '0 auto',
            md: '0 auto',
          }}
        >
          <Heading as='h2' marginBottom='16px' size='marketing-5xl'>
            {title}
          </Heading>
          <Text>{description}</Text>
        </Box>

        {video && (
          <>
            <Box h='48px' />
            <Box w='full'>
              <YouTube
                iframeClassName='youtube-container'
                title={video.title}
                videoId={video.youTubeVideoId}
                opts={{
                  host: 'https://www.youtube-nocookie.com',
                  playerVars: {
                    rel: 0,
                  },
                }}
                onPlay={() =>
                  trackEvent({
                    name: 'Scholarship Page CTA Clicked',
                    type: 'click',
                    label: 'play_video_clicked',
                    value: video?.title ?? '',
                  })
                }
              />
            </Box>
          </>
        )}

        <Flex
          gap='40px'
          flexDir={{
            base: 'column',
            md: 'row',
          }}
          marginY={{
            base: '40px',
            md: '48px',
          }}
        >
          <Benefits benefits={organizationBenefits} title='Benefits to your organization' />
          <Benefits benefits={communityBenefits} title='Benefits to your community' />
        </Flex>
        <ButtonLink
          buttonProps={{
            'aria-label': 'Sponsor a Scholarship',
            colorScheme: 'sea-foam',
            size: 'icon-right',
            rightIcon: <ArrowRightIcon height='24px' width='24px' />,
            onClick: () => {
              trackEvent({
                name: 'Scholarship Page CTA Clicked',
                type: 'click',
                label: 'sponsor_a_scholarship_clicked',
              });
            },
            width: {
              base: '100%',
              md: 'fit-content',
            },
          }}
          linkProps={{
            href: '/get-started/sponsor-a-scholarship',
          }}
        >
          Sponsor a Scholarship
        </ButtonLink>
      </ContentContainer>
    </Box>
  );
}

function Benefits({
  title,
  benefits,
}: {
  title: string;
  benefits: Array<{ _id: string; glyphName: string; title: string; description: string }>;
}) {
  return (
    <Box as='article' backgroundColor='gray.50' flex='1' padding='40px'>
      <Heading as='h3' marginBottom='24px' size='marketing-2xl'>
        {title}
      </Heading>
      <List display='flex' flexDir='column' gap='40px'>
        {benefits.map((benefit) => (
          <ListItem key={benefit._id}>
            <Flex alignItems='center' gap='16px'>
              <Glyph height={40} name={benefit.glyphName} width={40} />
              <Heading as='p' size='marketing-lg'>
                {benefit.title}
              </Heading>
            </Flex>
            <Text>{benefit.description}</Text>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
